/**
 * Per-product status → pill descriptor (Phase 65 / BLU-594).
 *
 * Maps the statuses the preview payload carries (`ready` | `unmapped_values` |
 * `unrecognised_option`, plus the `variantsTruncated` "never ready" flag and the
 * `duplicateSkus` collision guard) onto specific microcopy — e.g. an
 * `unmapped_values` product surfaces "1 colour unmapped" rather than a generic
 * "needs attention". Colour-less products are now a SUPPORTED ready state (they
 * derive via `colourlessCode`), so there is no colour-less status. Emerald =
 * ready; amber = attention. No new accent colours (DESIGN §1). Two tones, always
 * paired with text (DESIGN §16).
 */
import type { ByoDesignBlocker, ByoDesignWarning } from '@threadheads/shared';
import type { PreviewProduct } from './types';

/** Phase 86 slice B: the pill for each BYO blocker (literal keys, so the i18n check sees them). */
const BYO_PILL_KEYS: Record<ByoDesignBlocker, string> = {
  design_id_missing: 'byo.pill.design_id_missing',
  design_id_unreadable: 'byo.pill.design_id_unreadable',
  design_variant_missing: 'byo.pill.design_variant_missing',
  design_variant_unreadable: 'byo.pill.design_variant_unreadable',
};

const BYO_WARNING_KEYS: Record<ByoDesignWarning, string> = {
  design_sets_differ: 'byo.warning.design_sets_differ',
};

export type StatusTone = 'ready' | 'attention';

export interface StatusDescriptor {
  tone: StatusTone;
  /** i18n key in the `productSync` namespace. */
  key: string;
  /**
   * The key's interpolation values, always present (empty for a plain key):
   * `count` for the pluralised keys, `setUp` and `total` for the store SKU
   * partial pill.
   */
  values: Record<string, number>;
}

const descriptor = (tone: StatusTone, key: string, values: Record<string, number> = {}): StatusDescriptor => ({
  tone,
  key,
  values,
});

/**
 * Phase 88: in store SKU mode the server sends each product's `storeSku`
 * summary (88-05) and it alone decides the pill. It is never recomputed or
 * read from a SKU here (S16).
 */
function describeStoreSku(summary: NonNullable<PreviewProduct['storeSku']>): StatusDescriptor {
  switch (summary.status) {
    case 'set_up':
      return descriptor('ready', 'status.storeSkuSetUp');
    case 'needed':
      return descriptor('attention', 'status.storeSkuNeeded');
    case 'partial':
      // Nothing set up: "0 of 4 variants set up" reads like a count still
      // climbing, when none of them can be set up at all.
      if (summary.setUp === 0) return descriptor('attention', 'status.storeSkuPartialNone');
      return descriptor('attention', 'status.storeSkuPartial', { setUp: summary.setUp, total: summary.total });
    case 'changed':
      return descriptor('attention', 'status.storeSkuChanged');
    case 'new':
      return descriptor('attention', 'status.storeSkuNew');
  }
}

/**
 * Store SKU summary first when the server sent one; otherwise, priority:
 * unrecognised option > type gap > colour gap > size gap > duplicate collision.
 */
export function describeStatus(product: PreviewProduct): StatusDescriptor {
  if (product.storeSku) return describeStoreSku(product.storeSku);
  if (product.status === 'ready') {
    // 86-23: a store SKU BYO org's new product, ready to be made here (one
    // Product sync already made in full reads ready, as any derived row).
    if (product.storeSkuNew && product.syncedVariantCount < product.variantCount) {
      return descriptor('attention', 'status.storeSkuNew');
    }
    return descriptor('ready', 'status.ready');
  }
  if (product.variantsTruncated) {
    return descriptor('attention', 'status.truncated');
  }
  if (product.status === 'unrecognised_option') {
    return descriptor('attention', 'status.unrecognisedOption');
  }
  // Phase 86 slice B: an org using its own design IDs, and this product's
  // design ID or light / dark value can't be used. The server names why.
  if (product.status === 'byo_design_blocked' && product.byoDesign && !product.byoDesign.ok) {
    return descriptor('attention', BYO_PILL_KEYS[product.byoDesign.reason]);
  }
  // unmapped_values — pick the most upstream, most actionable reason.
  const { productTypes, colours, sizes } = product.unmappedValues;
  if (productTypes.length > 0) {
    return descriptor('attention', 'status.typeUnmapped');
  }
  if (colours.length > 0) {
    return descriptor('attention', 'status.colourUnmapped', { count: colours.length });
  }
  if (sizes.length > 0) {
    return descriptor('attention', 'status.sizeUnmapped', { count: sizes.length });
  }
  if (product.duplicateSkus.length > 0) {
    return descriptor('attention', 'status.duplicateSkus', { count: product.duplicateSkus.length });
  }
  return descriptor('attention', 'status.needsAttention');
}

/**
 * A product is colour-less when it declares no colour option (its variants all
 * have `colourName === null`). These derive READY using the org's
 * `colourlessCode` and use only the single print set.
 */
export function isColourless(product: PreviewProduct): boolean {
  return product.matchedOptions.colour === null && product.variantCount > 0;
}

/** Distinct colour VALUES seen across a product's variants, in first-seen order. */
export function distinctColours(product: PreviewProduct): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of product.variants) {
    if (!v.colourName) continue;
    const key = v.colourName.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v.colourName);
  }
  return out;
}

/** Distinct size VALUES seen across a product's variants, in first-seen (catalogue) order. */
export function distinctSizes(product: PreviewProduct): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const v of product.variants) {
    if (!v.sizeName) continue;
    const key = v.sizeName.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v.sizeName);
  }
  return out;
}

/** Phase 86 slice B (R3-9): the i18n key for a BYO warning (never a block). */
export function byoWarningKey(warning: ByoDesignWarning): string {
  return BYO_WARNING_KEYS[warning];
}
